// ─── SyncStatusPage ──────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { useAuth, canRunSync } from '../context/AuthContext';

const S = {
  page: { padding: '32px 36px', fontFamily: "'Inter', sans-serif" },
  title: { fontSize: 24, fontWeight: 600, color: 'var(--text1)', margin: '0 0 4px' },
  sub: { fontSize: 14, color: 'var(--text3)', margin: '0 0 28px' },
  topbar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  btnPrimary: { background: 'var(--purple)', color: '#fff', border: '1px solid var(--purple)' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 },
  stat: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' },
  statLabel: { fontSize: 12, color: 'var(--text3)', margin: '0 0 6px', fontWeight: 500 },
  statVal: { fontSize: 26, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  tableWrap: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' },
  td: { padding: '13px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', verticalAlign: 'middle' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
};

export function SyncStatusPage() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);

  useEffect(() => {
    fetch('/api/sync/history')
      .then(r => r.json())
      .then(d => { setRuns(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const triggerSync = async (type) => {
    setTriggering(type);
    await fetch(`/api/sync/${type}`, { method: 'POST' });
    const d = await fetch('/api/sync/history').then(r => r.json());
    setRuns(Array.isArray(d) ? d : []);
    setTriggering(null);
  };

  const lastRun = runs[0];
  const successRuns = runs.filter(r => r.status === 'success').length;

  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading sync history...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Sync Status</h1>
          <p style={S.sub}>History of LeaderCloud → QuickBooks sync runs</p>
        </div>
        {canRunSync(user) && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn} onClick={() => triggerSync('products')} disabled={!!triggering}>
              {triggering === 'products' ? '⟳ Syncing...' : '↻ Sync products'}
            </button>
            <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => triggerSync('run')} disabled={!!triggering}>
              {triggering === 'run' ? '⟳ Running...' : '↻ Run full sync'}
            </button>
          </div>
        )}
      </div>

      <div style={S.stats}>
        {[
          { label: 'Total runs',    val: runs.length },
          { label: 'Successful',    val: successRuns, color: 'var(--green)' },
          { label: 'Failed',        val: runs.filter(r => r.status === 'failed').length, color: 'var(--red)' },
          { label: 'Last run',      val: lastRun ? new Date(lastRun.started_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '—', small: true },
        ].map(s => (
          <div key={s.label} style={S.stat}>
            <p style={S.statLabel}>{s.label}</p>
            <p style={{ ...S.statVal, ...(s.color ? { color: s.color } : {}), ...(s.small ? { fontSize: 18, paddingTop: 4 } : {}) }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Scheduler info */}
      <div style={{ background: 'var(--bbg)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 24 }}>
        <div><span style={{ fontSize: 12, color: 'var(--text2)' }}>Hourly sync</span><br /><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--purple)' }}>Every hour at :00</span></div>
        <div><span style={{ fontSize: 12, color: 'var(--text2)' }}>Daily digest</span><br /><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--purple)' }}>8:00 AM Sydney</span></div>
        <div><span style={{ fontSize: 12, color: 'var(--text2)' }}>Next run</span><br /><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--purple)' }}>Automatic</span></div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Type</th>
              <th style={S.th}>Started</th>
              <th style={S.th}>Duration</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Invoices synced</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Errors</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run, i) => {
              const dur = run.completed_at && run.started_at
                ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
                : null;
              return (
                <tr key={i}>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: 'var(--bbg)', color: 'var(--blue)' }}>
                      {run.sync_type || 'invoice'}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: 'var(--text2)' }}>
                    {run.started_at ? new Date(run.started_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td style={{ ...S.td, color: 'var(--text3)' }}>{dur !== null ? `${dur}s` : '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: 'var(--text1)' }}>{run.invoices_synced ?? '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: run.errors > 0 ? 'var(--red)' : 'var(--text3)' }}>{run.errors ?? 0}</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, ...(run.status === 'success' ? { background: 'var(--gbg)', color: 'var(--green)' } : run.status === 'failed' ? { background: 'var(--rbg)', color: 'var(--red)' } : { background: 'var(--obg)', color: 'var(--orange)' }) }}>
                      {run.status || 'pending'}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: 'var(--text3)', fontSize: 12 }}>{run.note || '—'}</td>
                </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No sync runs yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── UsersPage ────────────────────────────────────────────────────────────────
import { canManageUsers } from '../context/AuthContext';

const ROLE_COLORS = {
  admin:      { bg: 'var(--tint)', color: 'var(--purple-bright)' },
  accountant: { bg: 'var(--gbg)', color: 'var(--green)' },
  bdm:        { bg: 'var(--bbg)', color: 'var(--blue)' },
  staff:      { bg: 'var(--bg3)', color: 'var(--text3)' },
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('staff');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch('/api/users')
      .then(r => r.json())
      .then(d => { setUsers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRoleChange = async (userId, role) => {
    await fetch(`/api/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
  };

  const handleAdd = async () => {
    setSaving(true);
    const res = await fetch('/api/users/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newEmail, role: newRole }),
    });
    if (res.ok) {
      const d = await fetch('/api/users').then(r => r.json());
      setUsers(Array.isArray(d) ? d : []);
      setNewEmail(''); setShowAdd(false);
    }
    setSaving(false);
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    await fetch(`/api/users/${userId}`, { method: 'DELETE' });
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  if (!canManageUsers(currentUser)) {
    return <div style={{ ...S.page, color: 'var(--text3)' }}>Access denied — Admin only.</div>;
  }
  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading users...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Users</h1>
          <p style={S.sub}>Manage portal access and role assignments</p>
        </div>
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => setShowAdd(true)}>+ Invite user</button>
      </div>

      {showAdd && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14, color: 'var(--text1)' }}>Invite new user</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input style={{ ...S.btn, flex: 1, cursor: 'text' }} placeholder="Microsoft 365 email address" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <select style={{ ...S.btn, cursor: 'pointer' }} value={newRole} onChange={e => setNewRole(e.target.value)}>
              <option value="staff">Staff</option>
              <option value="bdm">BDM</option>
              <option value="accountant">Accountant</option>
              <option value="admin">Admin</option>
            </select>
            <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleAdd} disabled={saving || !newEmail}>{saving ? 'Saving...' : 'Invite'}</button>
            <button style={S.btn} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>User will be able to log in with their Microsoft 365 account. They must be in your Azure tenant.</p>
        </div>
      )}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>User</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Last login</th>
              <th style={S.th}>Status</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const rc = ROLE_COLORS[u.role] || ROLE_COLORS.staff;
              const isSelf = u.id === currentUser?.id;
              return (
                <tr key={u.id}>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: rc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: rc.color, flexShrink: 0 }}>
                        {u.name?.charAt(0) || u.email?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{u.name || 'Unknown'}</div>
                        {isSelf && <span style={{ fontSize: 10, color: 'var(--purple)' }}>● You</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ ...S.td, color: 'var(--text2)' }}>{u.email}</td>
                  <td style={S.td}>
                    {isSelf ? (
                      <span style={{ ...S.badge, background: rc.bg, color: rc.color }}>{u.role}</span>
                    ) : (
                      <select style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, fontFamily: "'Inter', sans-serif", background: rc.bg, color: rc.color, fontWeight: 600 }}
                        value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}>
                        <option value="staff">Staff</option>
                        <option value="bdm">BDM</option>
                        <option value="accountant">Accountant</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                  </td>
                  <td style={{ ...S.td, color: 'var(--text3)', fontSize: 12 }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never'}
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, ...(u.is_active ? { background: 'var(--gbg)', color: 'var(--green)' } : { background: 'var(--bg3)', color: 'var(--text3)' }) }}>
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {!isSelf && (
                      <button style={{ ...S.btn, padding: '5px 10px', fontSize: 12, color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => handleDeactivate(u.id)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── AuditLogPage ─────────────────────────────────────────────────────────────
import { canSeeAuditLog } from '../context/AuthContext';

export function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch('/api/audit')
      .then(r => r.json())
      .then(d => { setLogs(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l =>
    l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.action?.toLowerCase().includes(search.toLowerCase()) ||
    l.details?.toLowerCase().includes(search.toLowerCase())
  );

  const ACTION_COLOR = {
    login:         { bg: 'var(--bbg)', color: 'var(--blue)' },
    sync:          { bg: 'var(--tint)', color: 'var(--purple-bright)' },
    mapping_saved: { bg: 'var(--gbg)', color: 'var(--green)' },
    price_updated: { bg: 'var(--obg)', color: 'var(--orange)' },
    user_invited:  { bg: 'var(--gbg)', color: 'var(--green)' },
    user_removed:  { bg: 'var(--rbg)', color: 'var(--red)' },
  };

  if (!canSeeAuditLog(user)) return <div style={{ ...S.page, color: 'var(--text3)' }}>Access denied.</div>;
  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading audit log...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Audit Log</h1>
          <p style={S.sub}>Every action logged with user and timestamp</p>
        </div>
      </div>

      <input style={{ ...S.btn, width: 280, cursor: 'text', marginBottom: 14 }} placeholder="Search actions, users..." value={search} onChange={e => setSearch(e.target.value)} />

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Time</th>
              <th style={S.th}>User</th>
              <th style={S.th}>Action</th>
              <th style={S.th}>Details</th>
              <th style={S.th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((log, i) => {
              const ac = ACTION_COLOR[log.action] || { bg: 'var(--bg3)', color: 'var(--text3)' };
              return (
                <tr key={i}>
                  <td style={{ ...S.td, color: 'var(--text3)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                  </td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 13 }}>{log.user_name || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{log.user_role || ''}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: ac.bg, color: ac.color }}>
                      {(log.action || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: 'var(--text2)', fontSize: 12 }}>{log.details || '—'}</td>
                  <td style={{ ...S.td, color: 'var(--text3)', fontSize: 11, fontFamily: 'monospace' }}>{log.ip_address || '—'}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No audit records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── NotificationsPage ────────────────────────────────────────────────────────
export function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/notifications')
      .then(r => r.json())
      .then(d => { setNotifications(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await fetch(`/api/notifications/${id}/read`, { method: 'PUT' });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await fetch('/api/notifications/read-all', { method: 'PUT' });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const TYPE_STYLE = {
    sync_success: { bg: 'var(--gbg)', color: 'var(--green)', icon: '✓' },
    sync_failed:  { bg: 'var(--rbg)', color: 'var(--red)', icon: '✕' },
    invoice:      { bg: 'var(--tint)', color: 'var(--purple-bright)', icon: '◻' },
    digest:       { bg: 'var(--bbg)', color: 'var(--blue)', icon: '◉' },
    warning:      { bg: 'var(--obg)', color: 'var(--orange)', icon: '⚠' },
  };

  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading notifications...</div>;

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Notifications {unread > 0 && <span style={{ fontSize: 16, background: 'var(--purple)', color: '#fff', borderRadius: 99, padding: '2px 8px', marginLeft: 8 }}>{unread}</span>}</h1>
          <p style={S.sub}>System alerts, sync results, and daily digests</p>
        </div>
        {unread > 0 && <button style={S.btn} onClick={markAllRead}>Mark all read</button>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {notifications.map(n => {
          const ts = TYPE_STYLE[n.type] || { bg: 'var(--bg3)', color: 'var(--text3)', icon: '●' };
          return (
            <div key={n.id} style={{ background: n.read ? 'var(--bg2)' : 'var(--card)', border: `1px solid ${n.read ? 'var(--border)' : 'var(--tint-line)'}`, borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start', cursor: n.read ? 'default' : 'pointer' }}
              onClick={() => !n.read && markRead(n.id)}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: ts.bg, color: ts.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                {ts.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: n.read ? 400 : 600, color: 'var(--text1)', fontSize: 14, marginBottom: 3 }}>{n.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{n.message}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                  {n.created_at ? new Date(n.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
              {!n.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--purple)', flexShrink: 0, marginTop: 6 }} />}
            </div>
          );
        })}
        {notifications.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 60, background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>◉</div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>No notifications yet</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Sync results and daily digests will appear here</div>
          </div>
        )}
      </div>
    </div>
  );
}
